import {
	BoldVerticalBarChartData,
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import { formatBoldVerticalChartData } from '@components/report/chart/utils';
import { VerticalBarAverageChartProps } from '@components/report/chart/vertical-bar-average-chart/VerticalBarAverageChart';

import { IGReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

export class AverageLikesAdapter {
	value: IGReportRes;

	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get isAvgLikePrivate() {
		return this.value?.engagement?.is_avg_like_private ?? false;
	}

	get avgLikes() {
		if (this.isAvgLikePrivate) return undefined;
		return this.value?.engagement?.avg_like ?? undefined;
	}

	get primaryText() {
		if (this.isAvgLikePrivate) return '비공개';
		else if (this.avgLikes === undefined) '- 개';
		else return `${round(this.avgLikes, 'down')} 개`;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				<>
					평균 좋아요 수는 <span>{this.primaryText}</span>입니다.
				</>
			),
			barChartValues: [60, 80, 70, 50, 40, 60, 75],
			value: this.avgLikes,
			isPositive: true,
		};
	}
}

export class AverageCommentsAdapter {
	value: IGReportRes;

	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get isAvgCommentPrivate() {
		return this.value?.engagement?.is_avg_comment_private ?? false;
	}

	get avgComments() {
		if (this.isAvgCommentPrivate) return undefined;
		return this.value?.engagement?.avg_comment ?? undefined;
	}

	get primaryText() {
		if (this.isAvgCommentPrivate) return '비활성화';
		else if (this.avgComments === undefined) return '- 개';
		else return `${round(this.avgComments, 'down')} 개`;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				<>
					평균 댓글 수는 <span>{this.primaryText}</span>입니다.
				</>
			),
			barChartValues: [30, 50, 35, 75, 80, 55, 40],
			value: this.avgComments,
			isPositive: true,
		};
	}
}

export class EngagementRateAdapter {
	value: IGReportRes;

	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	/**
	 * @description 참여율
	 */
	get realEngagement() {
		return (this.value?.engagement?.real_engagement ?? undefined) as number;
	}
	/**
	 * @description 유사 그룹 평균 참여율
	 */
	get avgRealEngagement() {
		return (this.value?.engagement?.avg_similar_group_real_engagement ??
			undefined) as number;
	}

	/**
	 * @description 비교 결과 텍스트
	 */
	textMapper = {
		high: (
			<>
				보다 <span>높습니다.</span>
			</>
		),
		normal: (
			<>
				과 <span>비슷합니다.</span>
			</>
		),
		low: (
			<>
				보다 <span>낮습니다.</span>
			</>
		),
		unmeasurable: (
			<>
				과 <span>비교 예정입니다.</span>
			</>
		),
	} as const;

	/**
	 * @description 비교 결과 상태
	 */
	get status() {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;
		if (!hasAllValue) return 'unmeasurable';

		return (this.realEngagement as number) > (this.avgRealEngagement as number) * 1.1
			? 'high'
			: (this.realEngagement as number) < (this.avgRealEngagement as number) * 0.9
			? 'low'
			: 'normal';
	}

	/**
	 * @description 인플루언서 참여율 차트 데이터 (주 데이터)
	 */
	get infAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;

		return {
			value: `${roundFloat(this?.realEngagement ?? 0, 'down', 2)}%`,
			label: this.value?.account?.profile?.username ?? '',
			valuePercentage: hasAllValue
				? (this.realEngagement / (this.realEngagement + this.avgRealEngagement)) * 100
				: undefined,
			colorType: this.status === 'high' ? 'primary' : 'black',
			isMain: true,
		} as const;
	}

	/**
	 * @description 유사그룹 평균 참여율 차트 데이터 (비교군 데이터)
	 */
	get similarGroupAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;

		return {
			value: `${roundFloat(this?.avgRealEngagement ?? 0, 'down', 2)}%`,
			label: '유사 그룹 평균',
			valuePercentage: hasAllValue
				? (this.avgRealEngagement / (this.realEngagement + this.avgRealEngagement)) * 100
				: undefined,
			colorType: 'gray',
		} as const;
	}

	/**
	 * @description 차트 데이터 전체
	 */
	get data(): BoldVerticalBarChartData[] {
		const realEngagementDefaultData = {
			label: this.value?.account?.profile?.username ?? '',
		};
		const avgRealEngagementDefaultData = {
			label: '유사 그룹 평균',
		};

		const realEngagementData = formatBoldVerticalChartData(
			this.realEngagement,
			realEngagementDefaultData,
			this.infAdvancedData,
		);
		const avgRealEngagementData = formatBoldVerticalChartData(
			this.avgRealEngagement,
			avgRealEngagementDefaultData,
			this.similarGroupAdvancedData,
		);

		return [realEngagementData, avgRealEngagementData];
	}

	/**
	 * @description 유효 팔로워 수 텍스트
	 */
	get valueText(): string {
		if (!this.realEngagement) return '데이터 준비 중';

		return `${roundFloat(this.realEngagement, 'down', 2)}%로`;
	}

	adapt(): BoldVerticalBarChartProps {
		return {
			desc: (
				<>
					참여율(ER) <span>{this.valueText}</span>
					<br />
					유사 그룹 평균{this.textMapper[this.status]}
				</>
			),
			data: this.data,
		};
	}
}

export class EngagementIgAdapter {
	value: IGReportRes;

	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	adapt(): {
		averageLikes: VerticalBarAverageChartProps;
		averageComments: VerticalBarAverageChartProps;
		engagementRate: BoldVerticalBarChartProps;
	} {
		return {
			averageLikes: new AverageLikesAdapter(this.value).adapt(),
			averageComments: new AverageCommentsAdapter(this.value).adapt(),
			engagementRate: new EngagementRateAdapter(this.value).adapt(),
		};
	}
}
